import React, { useState } from 'react';
import { Loader2, CalendarDays, Check } from 'lucide-react';

// Interview request in place of checkout, for classes the studio books by
// interview (product.inquiry). The fields match The Acting Center's own contact
// form: name, email, phone, message, how you heard about us. The first click on
// the button opens the form; submitting it posts to /inquiry, which emails the
// studio. Nothing is charged.
export function InquiryForm({ productId, buttonLabel, buttonStyle, studioName, defaults }: {
  productId: string;
  buttonLabel: string;
  buttonStyle: React.CSSProperties;
  studioName: string;
  defaults?: { name?: string; email?: string };
}) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: defaults?.name || '', email: defaults?.email || '', phone: '', message: '', heardFrom: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setF({ ...f, [k]: e.target.value }); if (error) setError(null); };
  const complete = f.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.phone.trim() && f.message.trim() && f.heardFrom.trim();

  const submit = async () => {
    if (!complete) { setError('Please fill in every field.'); return; }
    setState('sending');
    try {
      const r = await fetch(`/api/creator/products/${productId}/inquiry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || 'Could not send your request. Please try again.'); setState('idle'); return; }
      setState('sent');
    } catch {
      setError('Could not send your request. Please try again.');
      setState('idle');
    }
  };

  if (state === 'sent') {
    return (
      <div style={{ padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
        <p className="m-0 inline-flex items-center gap-2" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)' }}><Check className="w-4 h-4" /> Request sent</p>
        <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.5, marginTop: 6 }}>
          {studioName} has your details and will be in touch to set up your interview. We also emailed you a copy.
        </p>
      </div>
    );
  }

  const input: React.CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-2)', color: 'var(--hj-ink)', fontSize: 14, fontFamily: 'inherit' };
  const label = (t: string) => <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 6 }}>{t}</span>;
  return (
    <>
      {open && (
        <div style={{ marginBottom: 10, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
          <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.5, marginBottom: 12 }}>
            {studioName} meets with every student before the class begins. Send your details and they'll reach out to book your interview.
          </p>
          <div className="flex flex-col gap-3">
            <label>{label('Name')}<input type="text" value={f.name} onChange={set('name')} style={input} maxLength={120} autoComplete="name" /></label>
            <label>{label('Email')}<input type="email" value={f.email} onChange={set('email')} style={input} maxLength={200} autoComplete="email" /></label>
            <label>{label('Phone')}<input type="tel" value={f.phone} onChange={set('phone')} style={input} maxLength={40} autoComplete="tel" /></label>
            <label>{label('Message')}<textarea value={f.message} onChange={set('message')} rows={3} style={{ ...input, resize: 'vertical' }} maxLength={4000} placeholder="Tell them a little about yourself and your training" /></label>
            <label>{label('How did you hear about us?')}<input type="text" value={f.heardFrom} onChange={set('heardFrom')} style={input} maxLength={200} /></label>
          </div>
          {error && <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', marginTop: 10, fontWeight: 600 }}>{error}</p>}
        </div>
      )}
      <button
        type="button"
        onClick={open ? submit : () => setOpen(true)}
        disabled={state === 'sending'}
        className="w-full inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
        style={{ ...buttonStyle, opacity: state === 'sending' ? 0.6 : 1 }}
      >
        {state === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
        {open ? 'Send my request' : buttonLabel}
      </button>
    </>
  );
}
